import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, ShieldOff } from "lucide-react";

import { EmptyState, LoadingRows } from "../components/states";
import { RequireAuth } from "../features/auth/RequireAuth";
import { GovernancePage } from "../features/governance/GovernancePage";
import { useUserProfile } from "../features/profile/use-user-profile";

export const Route = createFileRoute("/governance")({
  component: () => (
    <RequireAuth>
      {(user) => (
        <GovernanceRoute
          uid={user.uid}
          email={user.email}
          signOut={user.signOut}
        />
      )}
    </RequireAuth>
  ),
});

function GovernanceRoute({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const { state } = useUserProfile(uid);

  if (state.status === "loading") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page">
        <div className="w-full max-w-2xl px-5">
          <LoadingRows rows={4} />
        </div>
      </main>
    );
  }

  if (state.status === "error" || state.status === "not-provisioned") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page p-6">
        <EmptyState icon={AlertCircle} title="Something went wrong">
          {state.status === "error"
            ? state.error
            : "Your account has not been provisioned yet."}
        </EmptyState>
      </main>
    );
  }

  if (state.profile.platformRole !== "super_admin") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page p-6">
        <EmptyState
          icon={ShieldOff}
          title="Access denied"
          testId="governance-access-denied"
        >
          Platform governance is available to super administrators only.
        </EmptyState>
      </main>
    );
  }

  return <GovernancePage uid={uid} email={email} signOut={signOut} />;
}
